import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { buildTagColorByName } from '@/features/shared/tags';
import { buildResultsFilesColumns } from '@/features/uploads/components/resultsFilesColumns';
import { useResultsFilesTableRowActions } from '@/features/uploads/components/useResultsFilesTableRowActions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { gridPaths, useTagOptions } from '@/shared/hooks';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { resultsFilesGridConfig } from '../grid/gridConfig';
import type { TagOption } from '@/shared/data/tags';
import type { SpacingToken } from '@/theme/tokens';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  DataGridProps,
  GridColumnVisibilityModel,
  GridSortModel,
  SxProps,
  Theme,
} from '@/ui/primitives';
import type { QueryResult, ResultsFileDto } from '@flowqa/shared';

type ResultsFilesTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  search?: string;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<ResultsFileDto>>;
  queryKey?: QueryKey;
  initialData?: QueryResult<ResultsFileDto>;
  isAuthenticated?: boolean;
  showLastUpdated?: boolean;
  onRowsChange?: (rows: ResultsFileDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: ResultsFileDto[] } | null) => void;
  filteredNoRowsText?: string;
  skeletonCellPaddingY?: SpacingToken;
  sx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  initialSortModel?: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  tagOptionsOverride?: TagOption[];
  refetchOnMount?: boolean | 'always';
  dynamicRowHeight?: boolean;
  preservePreviousData?: boolean;
  showSkeletonOnFetch?: boolean;
  showPaginationSkeletonWhenRowsVisible?: boolean;
  active?: boolean;
};

export const ResultsFilesTable = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions,
  initialPageSize,
  disablePageSizeSelect,
  search,
  noRowsText,
  fixedFilters,
  queryFn,
  queryKey,
  initialData,
  isAuthenticated,
  showLastUpdated = false,
  onRowsChange,
  onQueryMeta,
  filteredNoRowsText,
  skeletonCellPaddingY,
  sx,
  containerSx,
  initialSortModel,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  disableColumnSorting = true,
  disableColumnMenu = true,
  tagOptionsOverride,
  refetchOnMount,
  dynamicRowHeight = true,
  preservePreviousData = false,
  showSkeletonOnFetch = true,
  showPaginationSkeletonWhenRowsVisible = true,
  active = true,
}: ResultsFilesTableProps): ReactElement => {
  const { session, loading } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const resolvedPageSizeOptions = pageSizeOptions ?? [5, 10, 25, 50];
  const { selectedProjectId } = useProjectFilter();
  const [editTarget, setEditTarget] = useState<ResultsFileDto | null>(null);
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const {
    deleteLoading,
    deleteTarget,
    deleteRowPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onRestoreRow,
    onUpdateRowTagsColor,
    restoreRowPendingId,
    updateRowTagsColorPendingId,
  } = useResultsFilesTableRowActions({
    selectedProjectId,
  });
  const [shouldPoll, setShouldPoll] = useState(false);
  const handleRowsChange = useCallback(
    (rows: ResultsFileDto[]) => {
      const hasPending = rows.some((row) => row.status === 'queued' || row.status === 'processing');
      setShouldPoll(hasPending);
      onRowsChange?.(rows);
    },
    [onRowsChange],
  );
  const gridConfig = resultsFilesGridConfig({
    organisationId: selectedOrganisationId,
    persistKey: persistKey === undefined ? 'results-files-table' : persistKey,
    syncStateToUrl,
    fixedFilters,
    initialSortModel,
  });
  const tableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<ResultsFileDto>> => {
      return await (queryFn ?? gridConfig.queryFn)(input);
    },
    [gridConfig, queryFn],
  );
  const gridProps = buildTableGridProps<ResultsFileDto>({
    gridConfig,
    queryFn: tableQueryFn,
    queryKey,
    pageSizeOptions: resolvedPageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    noRowsText,
    showLastUpdated,
    onRowsChange: handleRowsChange,
  });
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns = useMemo(
    () =>
      buildResultsFilesColumns({
        editRowPendingId: updateRowTagsColorPendingId,
        deleteRowPendingId,
        onEditRow: (row) => {
          setEditTarget(row);
        },
        onDeleteRow,
        onRestoreRow,
        restoreRowPendingId,
        tagColorByName,
        updateRowTagsColorPendingId,
      }),
    [
      deleteRowPendingId,
      onDeleteRow,
      onRestoreRow,
      restoreRowPendingId,
      tagColorByName,
      updateRowTagsColorPendingId,
    ],
  );
  const togglableColumnFields = columns
    .map((column) => column.field)
    .filter((field) => field !== 'fileName');
  const editTargetName = editTarget?.fileName ?? '';

  return (
    <>
      <ServerDataTable<ResultsFileDto>
        queryPath={gridPaths.resultsFiles}
        {...gridConfig}
        {...gridProps}
        initialData={initialData}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={isAuthenticated ?? session !== null}
        externalLoading={loading}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        search={search}
        onQueryMeta={onQueryMeta}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        interactionMode="static"
        getRowHeight={dynamicRowHeight ? () => 'auto' : undefined}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={dynamicRowHeight ? null : undefined}
        filteredNoRowsText={filteredNoRowsText}
        skeletonCellPaddingY={skeletonCellPaddingY}
        refetchOnMount={refetchOnMount}
        preservePreviousData={preservePreviousData}
        showSkeletonOnFetch={showSkeletonOnFetch}
        showPaginationSkeletonWhenRowsVisible={showPaginationSkeletonWhenRowsVisible}
        active={active}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="fileName"], & .MuiDataGrid-cell[data-field="executionRunPassed"], & .MuiDataGrid-cell[data-field="suiteRunPassed"], & .MuiDataGrid-cell[data-field="testRunPassed"], & .MuiDataGrid-cell[data-field="details"], & .MuiDataGrid-cell[data-field="uploadId"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
            '& .MuiDataGrid-columnHeaders': {
              minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
            },
          },
          ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
        ]}
        containerSx={containerSx}
        disablePageSizeSelect={disablePageSizeSelect}
        autoHeight
        refetchIntervalMs={shouldPoll ? 5000 : false}
        gridProps={{
          getEstimatedRowHeight: dynamicRowHeight ? () => TABLE_ROW_MEASURE_HEIGHT : undefined,
          rowHeight: dynamicRowHeight ? undefined : TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Results File"
        entityName={editTargetName}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.id ?? null)}
        cascadeCopy="Tags will be applied to all associated execution runs, suite runs and test runs."
        onClose={() => {
          if (updateRowTagsColorPendingId === (editTarget?.id ?? null)) return;
          setEditTarget(null);
        }}
        onSave={({ tags, colors }) => {
          if (!editTarget) return;
          onUpdateRowTagsColor({ row: editTarget, tags, colors });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'resultsFile' })}
        description="This results file and its associated execution runs, suite runs and test runs will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'resultsFile',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
